import React from 'react'
import { cn } from '../../utils/cn'
import LoadingSpinner from './LoadingSpinner'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'destructive' | 'outline' | 'ghost' | 'danger' | 'default'
  size?: 'sm' | 'md' | 'lg' | 'xl'
  loading?: boolean
  children: React.ReactNode
}

const buttonVariants = {
  primary: 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:from-blue-600 hover:to-purple-700 shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 focus:ring-4 focus:ring-blue-300',
  secondary: 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-900 hover:from-gray-200 hover:to-gray-300 shadow-md hover:shadow-lg transform hover:scale-105 active:scale-95 focus:ring-4 focus:ring-gray-300',
  destructive: 'bg-gradient-to-r from-red-500 to-pink-600 text-white hover:from-red-600 hover:to-pink-700 shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 focus:ring-4 focus:ring-red-300',
  outline: 'border-2 border-purple-300 bg-white text-purple-700 hover:bg-purple-50 hover:border-purple-400 shadow-md hover:shadow-lg transform hover:scale-105 active:scale-95 focus:ring-4 focus:ring-purple-300',
  ghost: 'text-gray-700 hover:bg-gradient-to-r hover:from-purple-100 hover:to-pink-100 hover:text-purple-800 transform hover:scale-105 active:scale-95 focus:ring-4 focus:ring-purple-300',
  danger: 'bg-gradient-to-r from-red-500 to-pink-600 text-white hover:from-red-600 hover:to-pink-700 shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 focus:ring-4 focus:ring-red-300',
  default: 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:from-blue-600 hover:to-purple-700 shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 focus:ring-4 focus:ring-blue-300'
}

const buttonSizes = {
  sm: 'px-4 py-3 text-sm min-h-[44px] min-w-[44px]',
  md: 'px-6 py-4 text-base min-h-[48px] min-w-[48px]',
  lg: 'px-8 py-5 text-lg min-h-[56px] min-w-[56px]',
  xl: 'px-10 py-6 text-xl min-h-[64px] min-w-[64px]'
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ 
    className, 
    variant = 'primary', 
    size = 'md', 
    loading = false,
    disabled,
    children, 
    ...props 
  }, ref) => {
    return (
      <button
        className={cn(
          'inline-flex items-center justify-center rounded-2xl font-bold transition-all duration-300 ease-out',
          'focus:outline-none focus:ring-offset-2 font-fredoka',
          'disabled:opacity-50 disabled:pointer-events-none disabled:transform-none',
          'select-none touch-manipulation',
          '-webkit-tap-highlight-color: transparent',
          'active:transition-none',
          buttonVariants[variant as keyof typeof buttonVariants] || buttonVariants.primary,
          buttonSizes[size as keyof typeof buttonSizes] || buttonSizes.md,
          className
        )}
        disabled={disabled || loading}
        ref={ref}
        {...props}
      >
        {loading && (
          <LoadingSpinner 
            size={size === 'lg' ? 'md' : 'sm'} 
            color={variant === 'primary' || variant === 'destructive' ? 'white' : 'gray'}
            className="mr-2" 
          />
        )}
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'

export { Button }
export default Button